import { useState } from "react";
import { useTranslate } from "@refinedev/core";
import { Box, Chip, Divider, Grid2 as Grid, TextField, Typography } from "@mui/material";
import { Controller, useFormContext, useWatch } from "react-hook-form";

import { Form } from "@shared/components";

const DAYS = Array.from({ length: 31 }, (_, index) => index + 1);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function parseEmailList(value: string | null | undefined) {
  const seen = new Set<string>();
  const emails: string[] = [];
  String(value ?? "")
    .split(/[;,]/)
    .forEach((item) => {
      const email = item.trim();
      const key = email.toLowerCase();
      if (!email || seen.has(key)) return;
      seen.add(key);
      emails.push(email);
    });
  return emails;
}

function joinEmailList(emails: string[]) {
  return emails.join(", ");
}

function AgreementEmailList({
  name,
  label,
  hint,
}: {
  name: string;
  label: string;
  hint?: string;
}) {
  const translate = useTranslate();
  const { control } = useFormContext();
  const [draft, setDraft] = useState("");
  const [invalid, setInvalid] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => {
        const emails = parseEmailList(field.value);
        const error = invalid
          ? translate("v1/agreement_admin.errors.invalidEmail")
          : fieldState.error?.message;

        const commit = (raw: string) => {
          const parts = raw
            .split(/[;,\s]+/)
            .map((item) => item.trim())
            .filter(Boolean);
          if (parts.length === 0) {
            setDraft("");
            setInvalid(false);
            return;
          }
          if (parts.some((item) => !EMAIL_PATTERN.test(item))) {
            setInvalid(true);
            return;
          }
          const next = [...emails];
          parts.forEach((item) => {
            if (!next.some((email) => email.toLowerCase() === item.toLowerCase())) next.push(item);
          });
          field.onChange(joinEmailList(next));
          setDraft("");
          setInvalid(false);
        };

        return (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: hint ? 0 : 0.75 }}>
              {label}
            </Typography>
            {hint && (
              <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {hint}
              </Typography>
            )}
            <Box
              sx={{
                px: 1,
                py: 0.75,
                minHeight: 52,
                border: 1,
                borderColor: error ? "error.main" : "divider",
                borderRadius: 2,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.75,
                alignItems: "center",
                "&:focus-within": { borderColor: error ? "error.main" : "primary.main" },
              }}
            >
              {emails.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  size="small"
                  color="primary"
                  onDelete={() => field.onChange(joinEmailList(emails.filter((email) => email !== item)))}
                />
              ))}
              <TextField
                value={draft}
                placeholder={
                  emails.length === 0 ? translate("v1/agreement_admin.messages.email_placeholder") : ""
                }
                variant="standard"
                onBlur={() => {
                  field.onBlur();
                  if (draft.trim()) commit(draft);
                }}
                onChange={(event) => {
                  setInvalid(false);
                  setDraft(event.target.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === "," || event.key === ";") {
                    event.preventDefault();
                    commit(draft);
                  }
                  if (event.key === "Backspace" && draft === "" && emails.length > 0) {
                    field.onChange(joinEmailList(emails.slice(0, -1)));
                  }
                }}
                onPaste={(event) => {
                  const text = event.clipboardData.getData("text");
                  if (!/[;,\n]/.test(text)) return;
                  event.preventDefault();
                  commit(`${draft}${text}`);
                }}
                slotProps={{ input: { disableUnderline: true } }}
                sx={{ flex: 1, minWidth: 220 }}
              />
            </Box>
            {error && (
              <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                {error}
              </Typography>
            )}
          </Box>
        );
      }}
    />
  );
}

export function FinancialVolumetryFields() {
  const translate = useTranslate();
  const automatic = useWatch({ name: "volumetry_email_automatic" });

  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12 }}>
        <Typography style={{ fontSize: 18 }}>
          {translate("v1/agreement_admin.titles.financial")}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Form.Input
          name="cnpj"
          label={translate("v1/agreement_admin.fields.cnpj")}
          mask="cnpj"
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Form.Input
          name="razao_social"
          label={translate("v1/agreement_admin.fields.razao_social")}
        />
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Form.Input
          name="nome_fantasia"
          label={translate("v1/agreement_admin.fields.nome_fantasia")}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <AgreementEmailList
          name="provider_owner_email"
          label={translate("v1/agreement_admin.fields.provider_owner_email")}
        />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Divider />
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Typography style={{ fontSize: 18 }}>
          {translate("v1/agreement_admin.titles.volumetry")}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {translate("v1/agreement_admin.messages.volumetry_hint")}
        </Typography>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <AgreementEmailList
          name="financial_email"
          label={translate("v1/agreement_admin.fields.financial_email")}
          hint={translate("v1/agreement_admin.messages.financial_email_hint")}
        />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap" }}>
          <Form.Switch
            name="volumetry_email_automatic"
            label={translate("v1/agreement_admin.fields.volumetry_email_automatic")}
          />
          <Box sx={{ width: 200, maxWidth: "100%" }}>
            <Form.Select
              name="volumetry_email_day"
              label={translate("v1/agreement_admin.fields.volumetry_email_day")}
              required={!!automatic}
              options={[
                { value: "", label: "—" },
                ...DAYS.map((day) => ({ value: day, label: String(day) })),
              ]}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
}
